/**
 * BudgetGauge — token-driven linear gauge for one budget window (P3). Fill width
 * tracks spend/ceiling; fill colour follows the soft-alert `usd_status`
 * (under/approaching/over) via `--g-*` tokens. `data-status` exposes the level
 * for tests + parent styling. NO request-path throttling — display only.
 */
import { type ReactElement } from "react";
import type { BudgetWindowStatus } from "@/features/admin/ai-usage/api";

const STATUS_BG: Record<string, string> = {
  under: "var(--g-primary)",
  approaching: "var(--g-warn, #b8860b)",
  over: "var(--g-danger, #b91c1c)",
};

export function BudgetGauge({ status, label }: { status: BudgetWindowStatus; label: string }): ReactElement {
  const pct = status.ceiling_usd > 0 ? Math.min(100, (status.spend_usd / status.ceiling_usd) * 100) : 0;
  return (
    <div data-status={status.usd_status} className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-[var(--g-secondary)]">
          ${status.spend_usd.toFixed(2)} / ${status.ceiling_usd.toFixed(2)}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--g-elevated)]" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-label={label}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: STATUS_BG[status.usd_status] }} />
      </div>
    </div>
  );
}
export default BudgetGauge;
